import { useState, useRef, useEffect, Suspense } from 'react';
import Image from 'next/image';
import { styled } from '@linaria/react';
import { Button, PlayCircleIcon } from '@vidbaseapp/styleguide';
import { Canvas, useFrame } from '@react-three/fiber';
import { useDetectGPU, Points, PointMaterial } from '@react-three/drei';
import { EffectComposer, DepthOfField } from '@react-three/postprocessing';
import { motion, AnimatePresence } from 'framer-motion';
import * as random from 'maath/random';
export const StyledLandingSplash = /*#__PURE__*/styled("main")({
  name: "StyledLandingSplash",
  class: "saq2o3y"
});
export function Stars({
  speed,
  ...props
}) {
  const ref = useRef<typeof Points>();
  const [sphere] = useState(() => random.inSphere(new Float32Array(26000), {
    radius: 15
  }));
  useFrame((_state, delta) => {
    // @ts-ignore
    // ref.current.rotation.x -= delta / speed / 100
    // @ts-ignore
    ref.current.rotation.y -= delta / speed;
  });
  return <>
			<group rotation={[0, 0, Math.PI / 2.55]}>
				<Points // @ts-ignore
      ref={ref} positions={sphere} stride={3} frustumCulled={false} {...props}>
					<PointMaterial // @ts-ignore
        transparent color="#ffffff" size={0.008} sizeAttenuation={true} depthWrite={false} />
				</Points>
			</group>

			<EffectComposer>
				<DepthOfField focusDistance={1.1} focalLength={0.35} bokehScale={1} />
			</EffectComposer>
		</>;
}
export const LandingBackground = ({
  speed,
  ...props
}) => {
  const GPUTier = useDetectGPU();
  const [shouldShowCanvas, setShouldShowCanvas] = useState(false);
  useEffect(() => {
    if (GPUTier.tier != 0 && !GPUTier.isMobile) {
      setShouldShowCanvas(true);
    }
  }, []);

  if (!shouldShowCanvas) {
    return <img className="hero-bg-nogpu" src="/img/landing-bg.png" alt="landing background for low performance devices" />;
  }

  return <Canvas mode="concurrent" camera={{
    position: [0, 0, 0]
  }}>
			<Stars speed={speed} />
			<ambientLight />
			{
      /* <Suspense fallback={null}>
      <Cloud
      	position={[0.25, 0, 0]}
      	scale={0.015}
      	args={[1, 2]}
      	opacity={0.5}
      	speed={0.006} // Rotation speed
      	width={0.05} // Width of the full cloud
      	depth={1.5} // Z-dir depth
      	segments={40} // Number of particles
      />
      <Cloud
      	position={[-0.07, -0.02, -0.02]}
      	scale={0.006}
      	args={[1, 2]}
      	opacity={0.5}
      	speed={0.008} // Rotation speed
      	width={0.035} // Width of the full cloud
      	depth={1.5} // Z-dir depth
      	segments={40} // Number of particles
      />
      </Suspense> */
    }
		</Canvas>;
};
const DEFAULT_SPEED = 100;
export const spring = {
  type: 'spring',
  stiffness: 500,
  damping: 30
};
export const splashParentVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 0.1,
      staggerChildren: 0.5
    }
  }
};
export const splashVariants = {
  hidden: {
    opacity: 0,
    y: -50
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
export const LandingSplash = () => {
  const [finalSpring, setFinalSpring] = useState(spring);
  const [speed, setSpeed] = useState(DEFAULT_SPEED);
  const [isHoveringVideo, setIsHoveringVideo] = useState(false);
  return <StyledLandingSplash className="hero" onMouseDown={e => {
    if (e.button == 0) {
      setSpeed(1 / 5);
    }
  }} onMouseUp={e => {
    if (e.button == 0) {
      setSpeed(DEFAULT_SPEED);
    }
  }} onBlur={() => {
    setSpeed(DEFAULT_SPEED);
  }}>
			<div className="hero-background">
				<Suspense fallback={null}>
					<LandingBackground speed={speed} />
				</Suspense>
			</div>
			<div className="hero-splash">
				<AnimatePresence>
					<div className="hero-splash-container">
						<motion.h1 variants={splashVariants} initial={'hidden'} animate={'visible'} transition={{ ...finalSpring,
            delay: 0.15
          }}>
							🎙️ Edit Podcasts &amp;
							<br />
							📺 YouTube Videos
							<br />
							in <span className="gradient-text">Minutes.</span>
						</motion.h1>
						<motion.p variants={splashVariants} initial={'hidden'} animate={'visible'} transition={{ ...finalSpring,
            delay: 0.25
          }}>
							Whether you create audio-only{' '}
							<a className="highlighter" href="/use-cases/podcasts">
								podcasts
							</a>
							,{' '}
							<a className="highlighter" href="/use-cases/youtube">
								YouTube videos
							</a>
							, or{' '}
							<a className="highlighter" href="/use-cases/marketing">
								marketing ads
							</a>{' '}
							- Vidbase enables you to focus entirely on telling amazing stories by automating the
							most tedious and boring parts of media editing so you can live your life.
						</motion.p>
						<motion.div className="flex" variants={splashVariants} initial={'hidden'} animate={'visible'} transition={{ ...finalSpring,
            delay: 0.35
          }}>
							<div className="cta buttonWrap">
								<Button as="a" href="https://vid.bs/signup" draggable={false}>
									Request Access {'->'}
								</Button>
							</div>
						</motion.div>
					</div>
				</AnimatePresence>
			</div>
			<div className="hero-screenshot">
				<div className="hero-screenshot-bg"></div>
				<motion.div className="hero-screenshot-figure" variants={splashVariants} initial={'hidden'} animate={'visible'} transition={{ ...finalSpring,
        delay: 0.55
      }}>
					<div className="img-container" onMouseOver={() => {
          setIsHoveringVideo(true);
        }} onMouseOut={() => {
          setIsHoveringVideo(false);
        }} onBlur={() => {
          setIsHoveringVideo(false);
        }}>
						<Image src="/img/vidbase-screenshot.png" alt="placeholder" width="1024" height="576" priority />
						<PlayCircleIcon className="playicon" size="10rem" color={isHoveringVideo == true ? 'var(--clr-text-bright)' : 'var(--clr-text)'} />
					</div>
				</motion.div>
			</div>
		</StyledLandingSplash>;
};

require("./LandingSplash.linaria.module.css!=!../../../../../../node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js!./LandingSplash.tsx");